/**
 * Loose-FK source-filter overlap detector.
 *
 * Two loose FKs on the same source column with overlapping source filters
 * would activate together on the same row — likely a misconfiguration when
 * the user's intent was polymorphic dispatch (e.g. Rails-style
 * `commentable_type = 'Post'` vs `'Photo'`). We detect this at save time
 * and confirm before persisting.
 *
 * The check is conservative: only reports `disjoint` when provably so. For
 * the common polymorphic case — each filter is a simple `field = value`
 * predicate on the same discriminator — disjointness is detected exactly.
 * Anything more complex (LIKE, ranges, IS NULL) collapses to `'any'` for
 * that field and the function falls back to assuming overlap.
 *
 * Semantics mirror `evaluateFilterGroupsAgainstRow` / `filterWhereBuilder`:
 *   - groups are AND-ed across the array
 *   - conditions within a group are OR-ed
 *   - empty filter (no groups) matches all rows
 */

import type { FilterCondition, FilterGroup } from '../../core/types/workspaceTypes';

type PositiveSet = readonly string[] | 'any' | 'unsat';

/**
 * Returns true if two source-filter expressions could match at least one
 * common row. Conservative: returns false only when provably disjoint.
 */
export function couldFiltersOverlap(
  a: FilterGroup[] | undefined,
  b: FilterGroup[] | undefined,
): boolean {
  const aGroups = a ?? [];
  const bGroups = b ?? [];
  // Empty filter on either side matches every row → overlap is guaranteed.
  if (aGroups.length === 0 || bGroups.length === 0) return true;

  const fieldsA = collectGroupsByField(aGroups);
  const fieldsB = collectGroupsByField(bGroups);

  // Only fields constrained on BOTH sides can prove disjointness. A field
  // constrained on one side and free on the other can always be satisfied
  // by the matching row's value.
  for (const [field, orGroupsA] of fieldsA) {
    const orGroupsB = fieldsB.get(field);
    if (!orGroupsB) continue;
    const setA = intersectAcrossGroups(orGroupsA);
    const setB = intersectAcrossGroups(orGroupsB);
    if (areDisjoint(setA, setB)) return false;
  }
  return true;
}

function collectGroupsByField(groups: FilterGroup[]): Map<string, FilterCondition[][]> {
  const out = new Map<string, FilterCondition[][]>();
  for (const g of groups) {
    const arr = out.get(g.field) ?? [];
    arr.push(g.conditions);
    out.set(g.field, arr);
  }
  return out;
}

function reduceOrToPositiveSet(ors: FilterCondition[]): PositiveSet {
  const vals: string[] = [];
  for (const c of ors) {
    if (c.operator === '=' || c.operator === 'lookup') {
      if (c.value === null || c.value === undefined) continue;
      vals.push(String(c.value));
    } else {
      // LIKE / != / ranges / IS NULL / IS NOT NULL / array / json — we
      // can't enumerate. Treat the OR group as matching any value.
      return 'any';
    }
  }
  if (vals.length === 0) return 'unsat';
  return Array.from(new Set(vals));
}

function intersectAcrossGroups(orsList: FilterCondition[][]): PositiveSet {
  let acc: PositiveSet = 'any';
  for (const ors of orsList) {
    acc = intersect(acc, reduceOrToPositiveSet(ors));
    if (acc === 'unsat') return 'unsat';
  }
  return acc;
}

function intersect(x: PositiveSet, y: PositiveSet): PositiveSet {
  if (x === 'unsat' || y === 'unsat') return 'unsat';
  if (x === 'any') return y;
  if (y === 'any') return x;
  const inter = x.filter((v) => y.includes(v));
  return inter.length === 0 ? 'unsat' : inter;
}

function areDisjoint(a: PositiveSet, b: PositiveSet): boolean {
  if (a === 'unsat' || b === 'unsat') return true;
  if (a === 'any' || b === 'any') return false;
  return !a.some((v) => b.includes(v));
}
